import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {UserEvaluationThunks} from './userEvaluationThunks';
import {DocumentTemplateThunks} from 'features/documentTemplate/documentTemplateThunks';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import {UserEvaluation} from 'types/userEvaluation';
import {DocumentTemplate} from 'types/documentTemplate';
import XIcon from 'assets/images/icons/XIcon';
import RadioInputHorizontal from 'components/inputs/RadioInputHorizontal';
import DateInput from 'components/inputs/DateInput';
import DynamicTextBlockInputs from 'components/documentTemplate/DynamicTextBlockInputs';
import DynamicFillOutOnCreationInputs from 'components/documentTemplate/DynamicFillOutOnCreationInputs';
import {TextBlockElementClass, DocumentTemplateElement} from 'types/documentTemplateElement';
import buildDocumentTemplateSnapshot from 'utils/buildDocumentTemplateSnapshot';

interface UserEvaluationFormProps {
  closeDrawer: () => void;
  isDrawerOpen: boolean;
  userId: string;
}

const UserEvaluationForm: React.FC<UserEvaluationFormProps> = ({
  closeDrawer,
  userId,
  isDrawerOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userEvaluation = useSelector(
    (state: RootState) => state.userEvaluation.userEvaluation,
  );
  const documentTemplates = useSelector(
    (state: RootState) => state.documentTemplate.documentTemplates,
  );
  const user = useSelector((state: RootState) => state.user.user);
  const [updatedUserEvaluation, setUpdatedUserEvaluation] = useState<
    Partial<UserEvaluation>
  >({
    ...userEvaluation,
    userId,
    status: 'missing',
  });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [textBlockElements, setTextBlockElements] = useState<
    TextBlockElementClass[]
  >([]);
  const [fillOutOnCreationElements, setFillOutOnCreationElements] = useState<
    DocumentTemplateElement[]
  >([]);
  const [elementValues, setElementValues] = useState<Record<string, string>>(
    {},
  );
  const [fillOutOnCreationValues, setFillOutOnCreationValues] = useState<Record<string, any>>(
    {},
  );

  useEffect(() => {
    const loadDocumentTemplates = async () => {
      try {
        await dispatch(
          DocumentTemplateThunks.index({
            page: 1,
            search: {where: {}},
            sortField: '',
            sortDirection: '',
            rowsPerPage: 100,
          }),
        );
      } catch (err) {
        console.error(err);
      }
    };
    loadDocumentTemplates();
  }, [dispatch]);

  useEffect(() => {
    const isFormValid =
      updatedUserEvaluation.documentTemplateId &&
      (updatedUserEvaluation.deadline === false ||
        updatedUserEvaluation.deadlineDate);

    const areTextBlockInputsFilled =
      textBlockElements.length === 0 ||
      textBlockElements.every(
        element => elementValues[element.id]?.trim() !== '',
      );

    const areFillOutOnCreationInputsFilled =
      fillOutOnCreationElements.length === 0 ||
      fillOutOnCreationElements.every(element => {
        const value = fillOutOnCreationValues[element.id];
        if (element.properties.required) {
          if (element.elementType === 'Dropdown' && element.properties.isMulti) {
            return Array.isArray(value) && value.length > 0;
          }
          return value !== '' && value !== false && value !== null && value !== undefined;
        }
        return true;
      });

    setIsSaveDisabled(!(isFormValid && areTextBlockInputsFilled && areFillOutOnCreationInputsFilled));
  }, [
    updatedUserEvaluation.documentTemplateId,
    updatedUserEvaluation.deadline,
    updatedUserEvaluation.deadlineDate,
    textBlockElements,
    elementValues,
    fillOutOnCreationElements,
    fillOutOnCreationValues,
  ]);

  useEffect(() => {
    setUpdatedUserEvaluation({
      ...userEvaluation,
      userId,
      status: userEvaluation?.status || 'missing',
    });
    setIsSaveDisabled(!userEvaluation?.documentTemplateId);
  }, [userEvaluation, userId]);

  useEffect(() => {
    if (updatedUserEvaluation.documentTemplateId) {
      const selectedTemplate = documentTemplates.find(
        (template: DocumentTemplate) =>
          template.id === updatedUserEvaluation.documentTemplateId,
      );

      if (selectedTemplate) {
        const textBlockElements: TextBlockElementClass[] = [];
        const fillOutOnCreationElements: DocumentTemplateElement[] = [];
        
        selectedTemplate.rows?.forEach((row: any) => {
          row.columns.forEach((column: any) => {
            if (column.element) {
              if (column.element.elementType === 'TextBlock') {
                textBlockElements.push(column.element as TextBlockElementClass);
              } else if (column.element.properties?.fillOutOnCreation) {
                fillOutOnCreationElements.push(column.element as DocumentTemplateElement);
              }
            }
          });
        });
        
        setTextBlockElements(textBlockElements);
        setFillOutOnCreationElements(fillOutOnCreationElements);
        setFillOutOnCreationValues({});
      }
    }
  }, [updatedUserEvaluation.documentTemplateId, documentTemplates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!updatedUserEvaluation.documentTemplateId) {
      console.error('Document template ID is required');
      return;
    }

    const documentTemplateSnapshot = buildDocumentTemplateSnapshot(
      documentTemplates,
      updatedUserEvaluation.documentTemplateId,
      elementValues,
      fillOutOnCreationValues,
    );

    if (!documentTemplateSnapshot) {
      console.error('Template not found');
      return;
    }

    const solutionAttributes = {
      documentTemplateId: updatedUserEvaluation.documentTemplateId,
      snapshot: documentTemplateSnapshot,
    };

    try {
      if (userEvaluation?.id) {
        await dispatch(
          UserEvaluationThunks.update({
            ...updatedUserEvaluation,
            id: userEvaluation.id,
            documentTemplateSolutionAttributes: solutionAttributes,
          } as UserEvaluation),
        ).then(() => closeDrawer());
      } else {
        const evaluationData = {
          userId: userId,
          status: 'missing',
          documentTemplateId: updatedUserEvaluation.documentTemplateId,
          deadline: updatedUserEvaluation.deadline,
          deadlineDate: updatedUserEvaluation.deadlineDate,
          documentTemplateSolutionAttributes: solutionAttributes,
        };

        await dispatch(
          UserEvaluationThunks.create(evaluationData as UserEvaluation),
        ).then(() => closeDrawer());
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleRadioChange = (field: string, value: boolean) => {
    setUpdatedUserEvaluation({...updatedUserEvaluation, [field]: value});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedUserEvaluation({
      ...updatedUserEvaluation,
      [e.target.id]: e.target.value,
    });
  };

  const handleElementUpdate = (elementId: string, content: string) => {
    setElementValues(prev => ({
      ...prev,
      [elementId]: content,
    }));
  };

  const handleFillOutOnCreationUpdate = (elementId: string, value: any) => {
    setFillOutOnCreationValues(prev => ({
      ...prev,
      [elementId]: value,
    }));
  };

  useEffect(() => {
    if (!isDrawerOpen) handleClose();
  }, [isDrawerOpen]);

  const handleClose = () => {
    setUpdatedUserEvaluation({
      deadlineDate: '',
      documentTemplateId: '',
    });
    setTextBlockElements([]);
    setFillOutOnCreationElements([]);
    setElementValues({});
    setFillOutOnCreationValues({});
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {userEvaluation?.id ? 'Edit Evaluation' : 'Create new Evaluation'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {userEvaluation?.id ? 'Edit' : 'Create'} an Evaluation for a
              document
            </div>
          </div>
          <button
            onClick={closeDrawer}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="h-full px-6 pb-6">
          <div className="mt-5">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col space-y-6 items-center mt-7">
                <div className="w-full">
                  <div className="flex flex-col gap-[0.625rem]">
                    <label
                      htmlFor="documentTemplateId"
                      className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white ">
                      Document
                    </label>
                    <CustomSelect
                      options={documentTemplates.map(
                        (template: DocumentTemplate) => ({
                          label: template.name,
                          value: template.id,
                        }),
                      )}
                      onChange={selectedOption => {
                        setUpdatedUserEvaluation({
                          ...updatedUserEvaluation,
                          documentTemplateId: selectedOption.value,
                        });
                      }}
                      value={updatedUserEvaluation?.documentTemplateId}
                    />
                    <div className="space-y-6 items-center mt-7">
                      <RadioInputHorizontal
                        label="Deadline"
                        options={[
                          {id: 'DeadlineYes', label: 'Yes', value: true},
                          {id: 'DeadlineNo', label: 'No', value: false},
                        ]}
                        selectedValue={updatedUserEvaluation.deadline}
                        onChange={value => handleRadioChange('deadline', value)}
                      />
                      {updatedUserEvaluation.deadline && (
                        <DateInput
                          id="deadlineDate"
                          label="Deadline Date"
                          type="date"
                          value={updatedUserEvaluation.deadlineDate || ''}
                          onChange={handleChange}
                          placeholder="MM/DD/YYYY"
                        />
                      )}
                    </div>
                    {textBlockElements.length > 0 && user && (
                      <div className="mt-6">
                        <label className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white mb-4 mt-4">
                          Please fill out the following fields
                        </label>
                        <DynamicTextBlockInputs
                          elements={textBlockElements}
                          onUpdateElement={handleElementUpdate}
                          user={user}
                          onUpdateAllInputsFilled={() => {}}
                        />
                      </div>
                    )}
                    {fillOutOnCreationElements.length > 0 && user && (
                      <div className="mt-6">
                        <label className="block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white mb-4 mt-4">
                          Please fill out the following fields
                        </label>
                        <DynamicFillOutOnCreationInputs
                          elements={fillOutOnCreationElements}
                          onUpdateElement={handleFillOutOnCreationUpdate}
                          user={user}
                          onUpdateAllInputsFilled={() => {}}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-2 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label="Submit"
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={closeDrawer}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserEvaluationForm;
